import { useEffect, useState, useRef } from "react";
import { createPopper } from "@popperjs/core";
import { ChevronDown, Check, Search, X } from "lucide-react";

export default function FlexibleSelect({
  dropdownData = [],
  selectedValue,
  onChange = () => {},
  placeholder = "Select an option",
  isSearch = false,
  placement = "bottom-start", // Popper placement values
  dropdownClass = "",
  dropdownButtonClass = "",
  optionsClass = "",
  onSearchHandler = () => {},
  onScroll = () => {},
  loading = false,
  label,
  labelClasses = "",
  errorType,
  errorText,
  required = false,
  isDisabled = false,
  checked = true,
  buttonStyle = "",
  searchValue = "",
  type = "single",
  displayKey = "name",
  valueKey = "value",
  imageKey = "image",
  isDropdownOpen = false,
  ...props
}) {
  const [selected, setSelected] = useState(type === "multiple" ? [] : null);
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(isDropdownOpen || false);
  const containerRef = useRef(null);
  const referenceRef = useRef(null);
  const popperRef = useRef(null);
  const popperInstanceRef = useRef(null);

  // Initialize Popper
  useEffect(() => {
    if (referenceRef.current && popperRef.current && open) {
      popperInstanceRef.current = createPopper(
        referenceRef.current,
        popperRef.current,
        {
          placement,
          modifiers: [
            {
              name: "preventOverflow",
              options: {
                boundary: "viewport",
                padding: 8,
              },
            },
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
          ],
        }
      );
    }

    return () => {
      if (popperInstanceRef.current) {
        popperInstanceRef.current.destroy();
        popperInstanceRef.current = null;
      }
    };
  }, [open, placement]);

  useEffect(() => {
    setList(dropdownData);
  }, [JSON.stringify(dropdownData)]);

  useEffect(() => {
    if (
      (selectedValue || selectedValue !== undefined) &&
      dropdownData &&
      Array.isArray(dropdownData) &&
      dropdownData?.length > 0
    ) {
      if (type === "multiple") {
        const matched = dropdownData.filter(
          (item) =>
            selectedValue.includes(item[valueKey]) ||
            selectedValue.includes(item._id)
        );
        setSelected(matched);
      } else {
        setSelected(
          dropdownData.find(
            (item) =>
              item?.[valueKey] === selectedValue || item?._id === selectedValue
          ) || null
        );
      }
    } else {
      setSelected(type === "multiple" ? [] : null);
    }
  }, [JSON.stringify(selectedValue), JSON.stringify(dropdownData)]);

  const handleClickOutside = (event) => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    if (type === "multiple") {
      const already = selected.some((sel) => sel[valueKey] === item[valueKey]);
      const updated = already
        ? selected.filter((sel) => sel[valueKey] !== item[valueKey])
        : [...selected, item];
      setSelected(updated);
      onChange(updated.map((v) => v[valueKey]));
    } else {
      setSelected(item);
      setOpen(false);
      onChange(item[valueKey]);
    }
  };

  const clearSelection = () => {
    setSelected(type === "multiple" ? [] : null);
    onChange(type === "multiple" ? [] : null);
  };

  const displayText = () => {
    if (type === "multiple") {
      if (!Array.isArray(selected) || selected.length === 0) return placeholder;
      return `${selected.length} selected`;
    }
    if (!selected) return placeholder;
    return (
      <div className="flex items-center gap-2">
        {selected?.[imageKey] && (
          <img
            src={selected[imageKey]}
            alt=""
            className="w-6 h-6 rounded-full"
          />
        )}
        <span>{selected[displayKey]}</span>
      </div>
    );
  };

  const filteredList = list.filter((option) =>
    option?.[displayKey]?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelectAll = () => {
    if (type === "multiple") {
      const all = list.filter((option) =>
        option?.[displayKey]?.toLowerCase().includes(search.toLowerCase())
      );
      setSelected(all);
      onChange(all.map((v) => v[valueKey]));
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${props.className || ""}`}
    >
      {label && (
        <label
          className={`text-xs font-medium text-text-disabled mb-1  ${labelClasses}`}
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div ref={referenceRef}>
        <button
          type="button"
          disabled={isDisabled}
          onClick={() => setOpen(!open)}
          className={`w-full h-10 flex items-center gap-2 rounded-md bg-white border px-3 text-sm text-gray-800 ${
            isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
          } ${buttonStyle} ${dropdownButtonClass}`}
        >
          <div className="flex-grow truncate whitespace-nowrap">
            {typeof displayText() === "string" ? displayText() : displayText()}
          </div>
          <ChevronDown
            className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

      {open && (
        <div
          ref={popperRef}
          className={`z-20 bg-white border w-full rounded-md shadow-md min-w-[200px] ${dropdownClass}`}
          onScroll={onScroll}
        >
          {/* Header with controls (only for multiple) */}
          {type === "multiple" && (
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
              <span className="text-xs text-gray-500">
                {selected.length} of {list.length} selected
                {search && ` (${filteredList.length} filtered)`}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-xs text-blue-500 hover:text-blue-700"
                >
                  Select All{search && " Filtered"}
                </button>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-xs text-red-500 hover:text-red-700"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
          {isSearch && (
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    onSearchHandler(e.target.value);
                  }}
                  placeholder="Search..."
                  className="w-full pl-8 pr-2 py-2 text-sm border rounded-md"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearch("");
                      onSearchHandler("");
                    }}
                    className="absolute right-2 top-2.5 text-gray-400 hover:text-black"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          <div className={`max-h-60 overflow-auto ${optionsClass}`}>
            {loading ? (
              <div className="text-center p-3 text-sm text-gray-500">
                Loading...
              </div>
            ) : filteredList.length === 0 ? (
              <div className="text-center p-3 text-sm text-gray-500">
                No options found
              </div>
            ) : (
              filteredList.map((item) => {
                const isSelected =
                  type === "multiple"
                    ? selected.some((sel) => sel[valueKey] === item[valueKey])
                    : selected?.[valueKey] === item[valueKey];
                return (
                  <div
                    key={item[valueKey]}
                    onClick={() => handleSelect(item)}
                    className={`flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${optionsClass}`}
                  >
                    {type === "multiple" && (
                      <div className="w-4 h-4 mr-2">
                        {isSelected ? (
                          <Check className="h-4 w-4 text-blue-600" />
                        ) : (
                          <div className="w-4 h-4 border border-gray-400 rounded" />
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-grow">
                      {item?.[imageKey] && (
                        <img
                          src={item[imageKey]}
                          alt=""
                          className="w-6 h-6 rounded-full"
                        />
                      )}
                      <span
                        className={`${
                          isSelected ? "font-medium text-blue-600" : ""
                        }`}
                      >
                        {item[displayKey]}
                      </span>
                    </div>
                    {type === "single" && isSelected && (
                      <Check className="h-4 w-4 text-blue-600" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {errorType && errorText && (
        <div
          className={`text-xs mt-1 ${
            errorType === "error"
              ? "text-red-600"
              : errorType === "warning"
              ? "text-amber-600"
              : errorType === "success"
              ? "text-green-600"
              : "text-sky-600"
          }`}
        >
          <i
            className={`fa-regular fa-fw text-sm mr-1 ${
              errorType === "error"
                ? "fa-square-exclamation"
                : errorType === "warning"
                ? "fa-triangle-exclamation"
                : errorType === "success"
                ? "fa-circle-check"
                : "fa-circle-info"
            }`}
          />
          {errorText}
        </div>
      )}
    </div>
  );
}
